/**
 * Memory accounting for the 150MB mobile budget.
 * performance.measureUserAgentSpecificMemory() (needs cross-origin isolation) covers the page
 * AND its dedicated workers; performance.memory is the Chrome-only JS-heap fallback.
 */

export interface MemorySample {
  bytes: number;
  source: 'measureUserAgentSpecificMemory' | 'performance.memory' | 'unavailable';
  breakdown?: { bytes: number; types: string[]; scope: string }[];
  at: number;
}

interface UAMemoryResult {
  bytes: number;
  breakdown: { bytes: number; attribution: { url: string; scope: string }[]; types: string[] }[];
}

export const MEMORY_BUDGET_BYTES = 150 * 1024 * 1024;

export async function measureMemory(): Promise<MemorySample> {
  const perf = performance as Performance & {
    measureUserAgentSpecificMemory?: () => Promise<UAMemoryResult>;
    memory?: { usedJSHeapSize: number };
  };
  if (typeof perf.measureUserAgentSpecificMemory === 'function' && crossOriginIsolated) {
    try {
      const r = await perf.measureUserAgentSpecificMemory();
      return {
        bytes: r.bytes,
        source: 'measureUserAgentSpecificMemory',
        breakdown: r.breakdown.map((b) => ({
          bytes: b.bytes,
          types: b.types,
          scope: b.attribution.map((a) => `${a.scope}:${a.url.split('/').pop()}`).join(',') || 'shared',
        })),
        at: Date.now(),
      };
    } catch {
      /* fall through */
    }
  }
  if (perf.memory) {
    return { bytes: perf.memory.usedJSHeapSize, source: 'performance.memory', at: Date.now() };
  }
  return { bytes: 0, source: 'unavailable', at: Date.now() };
}

export function formatMB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
